import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { VisualizationData } from '../../types';
import { formatNumber, cn } from '../../lib/utils';

interface SummaryCardProps {
  data: VisualizationData;
}

export function SummaryCard({ data }: SummaryCardProps) {
  const { config, data: cardData } = data;
  const { title } = config;

  const metrics = cardData.map((item) => {
    const keys = Object.keys(item);
    const labelKey =
      keys.find(
        (k) => typeof item[k] === 'string' && k.toLowerCase().includes('name')
      ) || keys[0];
    const valueKey = keys.find((k) => typeof item[k] === 'number') || keys[1];
    const changeKey = keys.find(
      (k) => k.toLowerCase().includes('change') || k.toLowerCase().includes('delta')
    );

    return {
      label: String(item[labelKey] || 'Metric'),
      value: Number(item[valueKey] || 0),
      change: changeKey ? Number(item[changeKey]) : undefined,
    };
  });

  const getTrendIcon = (change?: number) => {
    if (change === undefined) return null;
    if (change > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (change < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getChangeColor = (change?: number) => {
    if (change === undefined) return '';
    if (change > 0) return 'text-green-500';
    if (change < 0) return 'text-red-500';
    return 'text-muted-foreground';
  };

  return (
    <div className="w-full">
      {title && (
        <h3 className="mb-4 text-sm font-medium text-foreground">{title}</h3>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {metrics.map((metric, index) => (
          <div
            key={index}
            className="rounded-lg border border-border bg-card p-4 shadow-sm"
          >
            <p className="text-sm text-muted-foreground">{metric.label}</p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-bold text-foreground">
                {formatNumber(metric.value)}
              </span>
              {metric.change !== undefined && (
                <div
                  className={cn(
                    'flex items-center gap-1 text-sm',
                    getChangeColor(metric.change)
                  )}
                >
                  {getTrendIcon(metric.change)}
                  <span>
                    {metric.change > 0 ? '+' : ''}
                    {metric.change.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
